import { useEffect, useState } from 'react'

import { fvDate } from '@fv/dates'

export const tenderDateTimeFormat = 'MMM D, h:mm A'

const pad = (n: number) => String(n).padStart(2, '0')

const secondsUntil = (expiresMs: number | null) => {
  if (expiresMs === null) return null
  return Math.max(0, Math.ceil((expiresMs - Date.now()) / 1000))
}

const splitDuration = (totalSeconds: number) => ({
  hours: Math.floor(totalSeconds / 3600),
  minutes: Math.floor((totalSeconds % 3600) / 60),
  seconds: totalSeconds % 60,
})

/** `1:02:03` once past an hour, otherwise `02:03`. */
export const formatCountdown = (totalSeconds: number) => {
  const { hours, minutes, seconds } = splitDuration(totalSeconds)

  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(seconds)}`
    : `${pad(minutes)}:${pad(seconds)}`
}

/** `1h 2m 3s` / `2m 3s` / `3s`. */
export const formatCountdownLong = (totalSeconds: number) => {
  const { hours, minutes, seconds } = splitDuration(totalSeconds)

  return [
    hours && `${hours}h`,
    (hours || minutes) && `${minutes}m`,
    `${seconds}s`,
  ]
    .filter(Boolean)
    .join(' ')
}

export function useCountdown(expiresAt?: Date | string | null) {
  // Normalized so a fresh `Date` instance each render doesn't restart the tick.
  const expiresMs = expiresAt ? fvDate(expiresAt).valueOf() : null
  const [secondsRemaining, setSecondsRemaining] = useState(() =>
    secondsUntil(expiresMs),
  )

  useEffect(() => {
    const next = secondsUntil(expiresMs)
    setSecondsRemaining(next)
    if (next === null || next === 0) return

    const interval = setInterval(() => {
      const remaining = secondsUntil(expiresMs)
      setSecondsRemaining(remaining)
      if (remaining === 0) clearInterval(interval)
    }, 1000)

    return () => clearInterval(interval)
  }, [expiresMs])

  return {
    secondsRemaining,
    formatted:
      secondsRemaining === null ? null : formatCountdown(secondsRemaining),
    formattedLong:
      secondsRemaining === null ? null : formatCountdownLong(secondsRemaining),
    isExpired: secondsRemaining === 0,
  }
}
